import { LocalizationService } from '@abp/ng.core';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ConversationType } from 'src/core/models/generic-lookup-type/conversation/conversation-type.glt';
import { TextEditorConfigurationModel } from '../../filter-panel.module';
import { FilterPanelEditorConfigurationModel } from '../../models/filter-panel-editor-configuration.model';
import { FilterPanelEditorComponent } from '../../models/filter-panel-editor.component';
import { QuickSearchWithSideSelectionValue } from './model/quick-search-with-side-selection-value.model';

@Component({
  selector: 'ca-quick-search-with-side-selection-panel',
  templateUrl: './quick-search-with-side-selection-panel.component.html',
  styleUrls: ['./quick-search-with-side-selection-panel.component.scss'],
  standalone: false,
})
export class QuickSearchWithSideSelectionPanelComponent
  extends FilterPanelEditorComponent
  implements OnInit
{
  private _value: QuickSearchWithSideSelectionValue;

  actualConfig: TextEditorConfigurationModel;
  channelFilterVisible: boolean = false;
  channelSelectorDisabled: boolean = false;

  set value(value: QuickSearchWithSideSelectionValue) {
    this._value = value;

    this.editorForm.patchValue({
      sideId: value.sideId,
      quickSearchText: value.quickSearchText,
    });
  }

  get value(): QuickSearchWithSideSelectionValue {
    return {
      sideId: this.editorForm.controls.sideId.value,
      quickSearchText: this.editorForm.controls.quickSearchText.value,
    };
  }

  sideDisabled = false;

  set config(value: FilterPanelEditorConfigurationModel) {
    this.actualConfig = value as TextEditorConfigurationModel;
  }

  get config(): FilterPanelEditorConfigurationModel {
    return this.actualConfig;
  }

  get hasIcon(): boolean {
    return this.actualConfig.iconCls != null;
  }

  get labelClass() {
    let cls = '';
    if (this.actualConfig.callInline === true) {
      cls = 'col-5 custom-padding';
    } else if (this.actualConfig.chatInline === true) {
      cls = 'col-4 custom-padding';
    }
    return cls;
  }

  get customClass() {
    let cls = '';
    if (this.actualConfig.clearBottomMargin === true) {
      cls += 'mb-0';
    }
    if (this.actualConfig.callInline === true || this.actualConfig.chatInline === true) {
      cls += ' display-inline';
    }
    return cls;
  }

  get hideExternalIdInformation() {
    if (!this.actualConfig || this.actualConfig.hideExternalIdInformation == undefined) {
      return false;
    }

    return this.actualConfig.hideExternalIdInformation;
  }

  constructor(
    protected fb: FormBuilder,
    private localizationService: LocalizationService,
    private cdr: ChangeDetectorRef
  ) {
    super(fb);
    this.editorForm.addControl('sideId', new FormControl());
    this.editorForm.addControl('quickSearchText', new FormControl());

    this.editorForm.valueChanges.pipe(debounceTime(100)).subscribe(val => {
      this._value.sideId = val.sideId;
      this._value.quickSearchText = val.quickSearchText;
      this.onChange();
      this.checkFilterVisibilities();
    });
  }

  checkChannelFilterVisibile(): void {
    if (
      this.extraData.conversationType &&
      this.extraData.conversationType.length > 0 &&
      this.extraData.conversationType.indexOf(ConversationType.call) < 0 &&
      this.extraData.conversationType.indexOf(ConversationType.chat) < 0
    ) {
      this.channelFilterVisible = false;
    } else {
      this.channelFilterVisible = true;
    }
  }

  checkChannelSelectorDisabled(): void {
    if (
      !this.channelFilterVisible ||
      this._value?.quickSearchText.startsWith(
        this.localizationService.instant('::FilterByAgentLabel')
      ) ||
      this._value?.quickSearchText.startsWith(
        this.localizationService.instant('::FilterByCustomerLabel')
      )
    ) {
      this.channelSelectorDisabled = true;
    } else {
      this.channelSelectorDisabled = false;
    }
  }

  changeText(text: string) {
    const quickSearchText = this.editorForm.get('quickSearchText');
    this.sideDisabled = false;
    switch (text) {
      case 'AND': {
        quickSearchText.patchValue(
          this.localizationService.instant('::QuickSearch:OperatorAndExample')
        );
        break;
      }
      case 'OR': {
        quickSearchText.patchValue(
          this.localizationService.instant('::QuickSearch:OperatorOrExample')
        );
        break;
      }
      case 'NEAR': {
        quickSearchText.patchValue(
          this.localizationService.instant('::QuickSearch:OperatorNearExample')
        );
        break;
      }
      case 'NOT': {
        quickSearchText.patchValue(
          this.localizationService.instant('::QuickSearch:OperatorNotExample')
        );
        break;
      }
      case 'ag:': {
        quickSearchText.patchValue(this.localizationService.instant('::FilterByAgentLabel'));
        this.sideDisabled = true;
        break;
      }
      case 'cu:': {
        quickSearchText.patchValue(this.localizationService.instant('::FilterByCustomerLabel'));
        this.sideDisabled = true;
        break;
      }
      default: {
        quickSearchText.patchValue(this.localizationService.instant('::FilterByExtIdLabel'));
        break;
      }
    }

    if (this.hasIcon) {
      document.getElementById('quickSearchTextWithIcon').focus();
    } else {
      document.getElementById('quickSearchTextWithoutIcon').focus();
    }
  }

  ngOnInit(): void {}

  setExtraData(key: string, value: any): void {
    super.setExtraData(key, value);
    this.checkFilterVisibilities();
  }

  checkFilterVisibilities(): void {
    this.checkChannelFilterVisibile();
    this.checkChannelSelectorDisabled();

    this.cdr.detectChanges();
  }

  autoSelectFirstItem(): void {}

  init(): void {
    super.init();
  }

  reload(): void {}

  reset(): void {}

  onValueChange(eventArgs) {
    this.onChange();
  }
}
